import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Ticket, PlaneLanding, Phone, Info, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { AirlineLogo, SliceRow, FlightCard } from "./FlightCard";
import { Skeleton } from "./ui/skeleton";
import { http, money, dateLabel, dur, errMsg, instantOff } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const goToPartner = (part, offerId) => {
  http.post(`/flights/offer/${offerId}/redirect`, { book_with: part.book_with, price: part.price }).catch(() => {});
  const form = document.createElement("form");
  form.method = "POST"; form.action = part.url; form.target = "_blank";
  new URLSearchParams(part.post_data || "").forEach((v, k) => {
    const i = document.createElement("input"); i.type = "hidden"; i.name = k; i.value = v; form.appendChild(i);
  });
  document.body.appendChild(form); form.submit(); form.remove();
};

const SliceDetail = ({ s, label }) => (
  <div className="py-4 first:pt-0 border-b border-dashed last:border-0">
    <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">{label} · {dateLabel(s.depart_at)}</div>
    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
      <div className="flex items-center gap-3 sm:w-48">
        <AirlineLogo code={s.airline_code} logo={s.airline_logo} />
        <div><div className="text-sm font-semibold">{s.airline_name}</div><div className="text-xs text-slate-500">{s.flight_no}</div></div>
      </div>
      <SliceRow s={s} />
    </div>
    {s.segments?.length > 1 && (
      <div className="mt-3 ml-1 space-y-1 text-xs text-slate-500">
        {s.segments.map((g, i) => (
          <div key={i}>
            {g.flight_no} · {g.from} → {g.to} · {dur(g.duration_min || 0)}{g.airplane ? ` · ${g.airplane}` : ""}
            {s.layovers?.[i] && <div className="text-[#EC5B24] my-1">Layover at {s.layovers[i].airport} · {dur(s.layovers[i].duration_min || 0)}</div>}
          </div>
        ))}
      </div>
    )}
  </div>
);

const ReturnPicker = ({ offer }) => {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  useEffect(() => {
    http.get(`/flights/offer/${offer.id}/returns`).then((r) => setList(r.data)).catch((e) => { toast.error(errMsg(e)); setList([]); });
  }, [offer.id]);
  return (
    <section className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6" data-testid="return-picker">
      <h2 className="font-heading text-lg font-bold flex items-center gap-2 mb-1"><PlaneLanding className="h-5 w-5 text-[#EC5B24]" /> Choose your return flight</h2>
      <p className="text-sm text-slate-500 mb-4">Prices shown are round-trip totals for all travellers.</p>
      {!list && <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}</div>}
      <div className="space-y-3">
        {list?.map((o) => (
          <FlightCard key={o.id} offer={o} priceLabel="both flights · all travellers" price={o.total_amount}
            onBook={() => navigate(`/book/${o.id}`)} />
        ))}
      </div>
      {list && !list.length && <div className="text-sm text-slate-500" data-testid="no-returns">No return flights found. Try other dates.</div>}
    </section>
  );
};

export const PartnerOptions = ({ offer }) => {
  const [opts, setOpts] = useState(null);
  useEffect(() => {
    http.get(`/flights/offer/${offer.id}/booking-options`).then((r) => setOpts(r.data)).catch((e) => { toast.error(errMsg(e)); setOpts([]); });
  }, [offer.id]);
  return (
    <section className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6" data-testid="booking-options">
      <h2 className="font-heading text-lg font-bold flex items-center gap-2 mb-1"><Ticket className="h-5 w-5 text-[#EC5B24]" /> Where to buy this ticket</h2>
      <p className="text-sm text-slate-500 mb-4">Live prices from airlines and travel partners right now. Continue opens the partner site in a new tab.</p>
      {!opts && <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>}
      <div className="space-y-3">
        {opts?.map((o, i) => (
          <div key={i} className={`rounded-xl border p-4 ${i === 0 ? "border-[#0F9D58] bg-[#E8F5E9]/40" : "border-slate-200"}`} data-testid={`booking-option-${i}`}>
            {o.separate_tickets && <div className="text-[11px] font-bold uppercase tracking-wider text-amber-600 mb-2">Booked as separate tickets</div>}
            {o.parts.map((p, j) => (
              <div key={j} className="flex flex-wrap items-center gap-3 py-1">
                {p.logo ? <img src={p.logo} alt="" className="h-9 w-9 rounded-lg object-contain bg-white border" /> : <span className="h-9 w-9 rounded-lg bg-slate-100" />}
                <div className="flex-1 min-w-[140px]">
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    {p.book_with}
                    {p.is_airline && <span className="text-[10px] font-bold uppercase bg-indigo-50 text-indigo-600 rounded-full px-2 py-0.5">Airline</span>}
                    {i === 0 && j === 0 && <span className="text-[10px] font-bold uppercase bg-[#0F9D58] text-white rounded-full px-2 py-0.5">Cheapest</span>}
                  </div>
                  <div className="text-xs text-slate-500">
                    {[p.option_title, ...(p.baggage || [])].filter(Boolean).join(" · ")}
                  </div>
                </div>
                <div className="font-mono text-xl font-extrabold">{p.price ? money(p.price) : "—"}</div>
                {p.url ? (
                  <button data-testid={`partner-book-btn-${i}-${j}`} onClick={() => goToPartner(p, offer.id)}
                    className="rounded-full bg-[#EC5B24] hover:bg-[#D84A15] text-white text-sm font-bold px-5 py-2 flex items-center gap-1.5 transition-colors">
                    Continue <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                ) : p.phone ? (
                  <a href={`tel:${p.phone}`} className="rounded-full border px-4 py-2 text-sm font-semibold flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {p.phone}</a>
                ) : (
                  <span className="text-xs text-slate-400">Book on Google Flights</span>
                )}
              </div>
            ))}
          </div>
        ))}
        {opts && !opts.length && <div className="text-sm text-slate-500" data-testid="no-booking-options">No booking partners available for this fare right now.</div>}
      </div>
    </section>
  );
};

const LiveTotal = ({ offer }) => {
  const off = instantOff(offer.total_amount, useAuth().config);
  return (
    <div className="text-right">
      {off > 0 && <div className="text-xs text-slate-400 line-through font-mono">{money(offer.total_amount)}</div>}
      <div className="font-mono text-2xl font-extrabold text-[#EC5B24]" data-testid="live-total">{money(offer.total_amount - off)}</div>
      <div className="text-[11px] text-slate-500">{off > 0 ? `incl. ₹${off.toLocaleString("en-IN")} WanderNest instant off · all travellers` : "all travellers"}</div>
    </div>
  );
};

export const LiveBook = ({ offer, admin }) => {
  const pickReturn = offer.needs_return;
  return (
    <div className="mx-auto max-w-4xl px-4 py-6 space-y-5" data-testid="live-book-page">
      <section className="bg-white rounded-2xl border border-slate-100 p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-heading text-xl font-bold">Review your trip</h1>
          {!pickReturn && <LiveTotal offer={offer} />}
        </div>
        {offer.slices.map((s, i) => <SliceDetail key={i} s={s} label={i ? "Return" : "Departure"} />)}
      </section>
      {pickReturn ? <ReturnPicker offer={offer} /> : <PartnerOptions offer={offer} />}
      {!admin && (
      <div className="flex gap-3 text-xs text-slate-500 bg-white rounded-2xl border border-slate-100 p-4">
        <ShieldCheck className="h-4 w-4 text-[#0F9D58] shrink-0" />
        <span>Pick your return flight, then add traveller details and pay WanderNest by UPI. We book the ticket for you and share the PNR.</span>
      </div>
      )}
    </div>
  );
};
